import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Banknote, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import PayoutBankDetailsForm from '../components/PayoutBankDetailsForm';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Author/admin self-edit page for their own payout bank details.
 * Accessed from the profile dropdown ("Payout Bank Details").
 */
const PayoutBankDetails = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/me/payout-bank-details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          toast({
            title: 'Failed to load',
            description: e?.response?.data?.detail || 'Try again.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const onSubmit = async (form) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/me/payout-bank-details`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      toast({
        title: 'Bank details saved',
        description: res.data?.has_details
          ? 'Your account is now ready for the next quarterly payout.'
          : 'Saved as a draft. Add holder + bank + account/IBAN to be payout-ready.',
      });
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-6"
          data-testid="pbd-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Banknote className="h-5 w-5 text-emerald-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Payout Bank Details</h1>
            <p className="text-sm text-gray-400">
              Where Seven Chapter Books should send your quarterly royalty payments.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/15 px-4 py-2.5 my-5 flex items-center gap-2 text-xs text-emerald-200">
          <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Only you and the admin team can view these details. Stored securely and never shared
            outside the payout team.
          </span>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-16">Loading…</div>
        ) : (
          <PayoutBankDetailsForm
            key={data?.updated_at || 'new'}
            initial={data}
            onSubmit={onSubmit}
            submitting={submitting}
            helperNote={
              data?.updated_at
                ? `Last updated ${new Date(data.updated_at).toLocaleString()}.`
                : 'Not yet saved — fill in your details and click Save.'
            }
          />
        )}
      </div>
    </div>
  );
};

export default PayoutBankDetails;
